import React, { forwardRef, useImperativeHandle, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Alert,
} from "@mui/material";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Appointment } from "../../@data";
import { formatPrice, formatDuration } from "../../utils";

export interface CancelAppointmentModalRef {
  open: (appointment: Appointment) => void;
  close: () => void;
}

interface CancelAppointmentModalProps {
  onConfirm: (appointment: Appointment) => void;
  loading?: boolean;
}

const CancelAppointmentModal = forwardRef<
  CancelAppointmentModalRef,
  CancelAppointmentModalProps
>(({ onConfirm, loading = false }, ref) => {
  const [open, setOpen] = useState(false);
  const [appointment, setAppointment] = useState<Appointment | null>(null);

  useImperativeHandle(ref, () => ({
    open: (appointmentData: Appointment) => {
      setAppointment(appointmentData);
      setOpen(true);
    },
    close: () => {
      setOpen(false);
      setAppointment(null);
    },
  }));

  const handleClose = () => {
    if (!loading) {
      setOpen(false);
      setAppointment(null);
    }
  };

  const handleConfirm = () => {
    if (appointment) {
      onConfirm(appointment);
    }
  };

  const formatAppointmentDate = (timestamp: number) => {
    return format(timestamp, "EEEE, dd 'de' MMMM 'às' HH:mm", { locale: ptBR });
  };

  if (!appointment) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "1rem",
          padding: "1rem",
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" color="error">
          Cancelar Agendamento
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Você só pode cancelar agendamentos com pelo menos 1 hora de
          antecedência.
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            <strong>Serviço:</strong> {appointment.service.name}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Profissional:</strong> {appointment.professional.name}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Data e Horário:</strong>{" "}
            {formatAppointmentDate(appointment.timestamp)}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Valor:</strong> {formatPrice(appointment.service.price)}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Duração:</strong>{" "}
            {formatDuration(appointment.service.duration)}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary">
          Tem certeza que deseja cancelar este agendamento? Esta ação não pode
          ser desfeita.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ gap: 1 }}>
        <Button onClick={handleClose} disabled={loading} variant="outlined">
          Não, manter agendamento
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading}
          variant="contained"
          color="error"
        >
          {loading ? "Cancelando..." : "Sim, cancelar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

CancelAppointmentModal.displayName = "CancelAppointmentModal";

export default CancelAppointmentModal;
